import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
    Modal,
    Box,
    Typography,
    DialogContent,
    DialogActions,
    Button,
    Alert,
    FormControl,
    FormLabel,
    RadioGroup,
    FormControlLabel,
    Radio,
    TextField,
    Select,
    MenuItem,
    InputLabel,
    CircularProgress
} from '@mui/material';
import CheckIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorIcon from '@mui/icons-material/ErrorOutline';
import { useTranslation } from 'react-i18next';
import PaymentService from '../../Services/payment.service';

const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '90%',
    maxWidth: 600,
    bgcolor: 'background.paper',
    borderRadius: 3,
    boxShadow: 24,
    px: 4,
    py: 3
};

const ManualPaymentDialog = ({
    open,
    amount = 0,
    yearly = false,
    userId,
    userType,
    onSuccess,
    onClose
}) => {
    const { t, i18n } = useTranslation();
    const dir = i18n.dir();
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [paid, setPaid] = useState(false);
    const [paymentMethod, setPaymentMethod] = useState('Cash');
    const [referenceNumber, setReferenceNumber] = useState('');
    const [notes, setNotes] = useState('');

    const paymentMethods = [
        { value: 'Cash', label: 'Cash' },
        { value: 'BankTransfer', label: 'Bank Transfer' },
        { value: 'Check', label: 'Check' },
        { value: 'PayPal', label: 'PayPal' },
        { value: 'Other', label: 'Other' }
    ];

    const svc = +userType === 2 ? PaymentService.accountant : PaymentService.client;

    const handlePay = async () => {
        setBusy(true);
        setError('');

        const dto = {
            UserId: userId,
            UserType: userType,
            Amount: amount,
            Reference: referenceNumber || null,
            PaymentMethod: paymentMethod,
            Notes: notes || null
        };

        try {
            await svc.manual(dto);
            setPaid(true);
            setTimeout(onSuccess, 1500);
        } catch (ex) {
            setError(ex.message || t('PaymentFailed'));
        } finally {
            setBusy(false);
        }
    };

    const handleClose = () => {
        if (!busy) {
            setError('');
            setPaid(false);
            setPaymentMethod('Cash');
            setReferenceNumber('');
            setNotes('');
            onClose();
        }
    };

    return (
        <Modal open={open} onClose={handleClose} disableEscapeKeyDown>
            <Box sx={modalStyle} dir={dir}>
                <Typography variant="h6" gutterBottom>
                    {paid ? t('PaymentAccepted') : t('ManualPayment')}
                </Typography>

                <DialogContent dividers>
                    {paid ? (
                        <Alert severity="success" icon={<CheckIcon fontSize="inherit" />}>
                            {t('ThankYouReloading')}
                        </Alert>
                    ) : (
                        <>
                            <Typography variant="subtitle1" sx={{ mb: 2 }}>
                                {t('Amount')}: ₪{amount?.toFixed(2)} ({yearly ? 'Yearly' : 'Monthly'})
                            </Typography>

                            <FormControl component="fieldset" fullWidth sx={{ mb: 3 }}>
                                <FormLabel component="legend">{t('PaymentMethod')}</FormLabel>
                                <RadioGroup
                                    value={paymentMethod}
                                    onChange={(e) => setPaymentMethod(e.target.value)}
                                >
                                    {paymentMethods.map((method) => (
                                        <FormControlLabel
                                            key={method.value}
                                            value={method.value}
                                            control={<Radio />}
                                            label={method.label}
                                        />
                                    ))}
                                </RadioGroup>
                            </FormControl>

                            <TextField
                                fullWidth
                                label={t('ReferenceNumber')}
                                value={referenceNumber}
                                onChange={(e) => setReferenceNumber(e.target.value)}
                                placeholder={t('ReferenceNumberPlaceholder')}
                                sx={{ mb: 3 }}
                            />

                            <TextField
                                fullWidth
                                label={t('Notes')}
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                multiline
                                rows={3}
                                placeholder={t('PaymentNotesPlaceholder')}
                                sx={{ mb: 2 }}
                            />

                            {error && (
                                <Alert severity="error" icon={<ErrorIcon fontSize="inherit" />} sx={{ mt: 2 }}>
                                    {error}
                                </Alert>
                            )}
                        </>
                    )}
                </DialogContent>

                {!paid && (
                    <DialogActions>
                        <Button
                            onClick={handleClose}
                            color="secondary"
                            disabled={busy}
                        >
                            {t('Cancel')}
                        </Button>
                        <Button
                            onClick={handlePay}
                            variant="contained"
                            color="primary"
                            disabled={busy || !paymentMethod}
                            startIcon={busy && <CircularProgress size={20} />}
                        >
                            {t('ConfirmPayment')}
                        </Button>
                    </DialogActions>
                )}
            </Box>
        </Modal>
    );
};

ManualPaymentDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    amount: PropTypes.number,
    yearly: PropTypes.bool,
    userId: PropTypes.string.isRequired,
    userType: PropTypes.number.isRequired,
    onSuccess: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired
};

export default ManualPaymentDialog; 